import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Pencil, Save, X, Plus, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/contexts/SharedAuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface ReceptionContact {
  id: string;
  responsible: string;
  clapBase: string;
  data: string;
}

interface ComputerModel {
  id: string;
  profile: string;
  model: string;
}

interface ComputerRecovery {
  id: string;
  responsible: string;
  info: string;
}

export function UsefulInfoPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = user?.roles?.some(r => ['tech', 'admin', 'people', 'finance'].includes(r)) ?? false;

  // ── Reception contacts ──────────────────────────────────────────────────────
  const { data: contactsData, refetch: refetchContacts } = trpc.config.getReceptionContacts.useQuery();
  const saveContactsMutation = trpc.config.saveReceptionContacts.useMutation({
    onSuccess: () => { toast.success(t('devices.useful_info_page.save_success')); refetchContacts(); },
    onError: () => toast.error(t('devices.useful_info_page.save_error')),
  });

  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [editContactValues, setEditContactValues] = useState({ responsible: '', clapBase: '', data: '' });
  const [newContact, setNewContact] = useState<ReceptionContact | null>(null);
  const [newContactValues, setNewContactValues] = useState({ responsible: '', clapBase: '', data: '' });

  const contacts: ReceptionContact[] = contactsData?.contacts ?? [];

  const startEditContact = (contact: ReceptionContact) => {
    setNewContact(null);
    setEditingContactId(contact.id);
    setEditContactValues({ responsible: contact.responsible, clapBase: contact.clapBase, data: contact.data });
  };

  const saveContact = () => {
    const updated = contacts.map(c =>
      c.id === editingContactId ? { ...c, ...editContactValues } : c
    );
    saveContactsMutation.mutate({ contacts: updated });
    setEditingContactId(null);
  };

  const deleteContact = (id: string) => {
    saveContactsMutation.mutate({ contacts: contacts.filter(c => c.id !== id) });
  };

  const addContact = () => {
    setEditingContactId(null);
    setNewContactValues({ responsible: '', clapBase: '', data: '' });
    setNewContact({ id: Date.now().toString(), responsible: '', clapBase: '', data: '' });
  };

  const saveNewContact = () => {
    if (!newContact) return;
    const updated = [...contacts, { ...newContact, ...newContactValues }];
    saveContactsMutation.mutate({ contacts: updated });
    setNewContact(null);
  };

  // ── Computer models ──────────────────────────────────────────────────────────
  const { data: modelsData, refetch: refetchModels } = trpc.config.getComputerModels.useQuery();
  const saveModelsMutation = trpc.config.saveComputerModels.useMutation({
    onSuccess: () => { toast.success(t('devices.useful_info_page.save_success')); refetchModels(); },
    onError: () => toast.error(t('devices.useful_info_page.save_error')),
  });

  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [editModelValues, setEditModelValues] = useState({ profile: '', model: '' });
  const [newModel, setNewModel] = useState<ComputerModel | null>(null);
  const [newModelValues, setNewModelValues] = useState({ profile: '', model: '' });

  const models: ComputerModel[] = modelsData?.models ?? [];

  const startEditModel = (m: ComputerModel) => {
    setNewModel(null);
    setEditingModelId(m.id);
    setEditModelValues({ profile: m.profile, model: m.model });
  };

  const saveModel = () => {
    const updated = models.map(m =>
      m.id === editingModelId ? { ...m, ...editModelValues } : m
    );
    saveModelsMutation.mutate({ models: updated });
    setEditingModelId(null);
  };

  const deleteModel = (id: string) => {
    saveModelsMutation.mutate({ models: models.filter(m => m.id !== id) });
  };

  const addModel = () => {
    setEditingModelId(null);
    setNewModelValues({ profile: '', model: '' });
    setNewModel({ id: Date.now().toString(), profile: '', model: '' });
  };

  const saveNewModel = () => {
    if (!newModel) return;
    const updated = [...models, { ...newModel, ...newModelValues }];
    saveModelsMutation.mutate({ models: updated });
    setNewModel(null);
  };

  // ── Computer recoveries ──────────────────────────────────────────────────────
  const { data: recoveriesData, refetch: refetchRecoveries } = trpc.config.getComputerRecoveries.useQuery();
  const saveRecoveriesMutation = trpc.config.saveComputerRecoveries.useMutation({
    onSuccess: () => { toast.success(t('devices.useful_info_page.save_success')); refetchRecoveries(); },
    onError: () => toast.error(t('devices.useful_info_page.save_error')),
  });

  const [editingRecoveryId, setEditingRecoveryId] = useState<string | null>(null);
  const [editRecoveryValues, setEditRecoveryValues] = useState({ responsible: '', info: '' });
  const [newRecovery, setNewRecovery] = useState<ComputerRecovery | null>(null);
  const [newRecoveryValues, setNewRecoveryValues] = useState({ responsible: '', info: '' });

  const recoveries: ComputerRecovery[] = recoveriesData?.recoveries ?? [];

  const startEditRecovery = (r: ComputerRecovery) => {
    setNewRecovery(null);
    setEditingRecoveryId(r.id);
    setEditRecoveryValues({ responsible: r.responsible, info: r.info });
  };

  const saveRecovery = () => {
    const updated = recoveries.map(r =>
      r.id === editingRecoveryId ? { ...r, ...editRecoveryValues } : r
    );
    saveRecoveriesMutation.mutate({ recoveries: updated });
    setEditingRecoveryId(null);
  };

  const deleteRecovery = (id: string) => {
    saveRecoveriesMutation.mutate({ recoveries: recoveries.filter(r => r.id !== id) });
  };

  const addRecovery = () => {
    setEditingRecoveryId(null);
    setNewRecoveryValues({ responsible: '', info: '' });
    setNewRecovery({ id: Date.now().toString(), responsible: '', info: '' });
  };

  const saveNewRecovery = () => {
    if (!newRecovery) return;
    const updated = [...recoveries, { ...newRecovery, ...newRecoveryValues }];
    saveRecoveriesMutation.mutate({ recoveries: updated });
    setNewRecovery(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold tracking-tight">{t('devices.useful_info')}</h2>

      <Tabs defaultValue="reception">
        <TabsList>
          <TabsTrigger value="reception">{t('devices.useful_info_page.reception_tab')}</TabsTrigger>
          <TabsTrigger value="models">{t('devices.useful_info_page.models_tab')}</TabsTrigger>
          <TabsTrigger value="recoveries">{t('devices.useful_info_page.recoveries_tab')}</TabsTrigger>
        </TabsList>

        {/* Tab 1: Recepción de Computadoras */}
        <TabsContent value="reception">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('devices.useful_info_page.reception_tab')}</CardTitle>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={addContact}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('devices.useful_info_page.add_row')}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[160px]">{t('devices.useful_info_page.col_responsible')}</TableHead>
                      <TableHead className="w-[140px]">{t('devices.useful_info_page.col_clapbase')}</TableHead>
                      <TableHead>{t('devices.useful_info_page.col_data')}</TableHead>
                      {canEdit && <TableHead className="w-[100px] text-right">{t('devices.table_headers.actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map(contact => (
                      <TableRow key={contact.id}>
                        {editingContactId === contact.id ? (
                          <>
                            <TableCell>
                              <Input value={editContactValues.responsible} onChange={e => setEditContactValues(v => ({ ...v, responsible: e.target.value }))} />
                            </TableCell>
                            <TableCell>
                              <Input value={editContactValues.clapBase} onChange={e => setEditContactValues(v => ({ ...v, clapBase: e.target.value }))} />
                            </TableCell>
                            <TableCell>
                              <Textarea rows={5} value={editContactValues.data} onChange={e => setEditContactValues(v => ({ ...v, data: e.target.value }))} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" onClick={saveContact} disabled={saveContactsMutation.isPending} title={t('forms.submit')}>
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingContactId(null)} title={t('forms.cancel')}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="font-medium align-top">{contact.responsible}</TableCell>
                            <TableCell className="align-top">{contact.clapBase}</TableCell>
                            <TableCell className="text-sm whitespace-pre-line align-top">{contact.data}</TableCell>
                            {canEdit && (
                              <TableCell className="text-right align-top">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => startEditContact(contact)} title={t('devices.edit_title')}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteContact(contact.id)} disabled={saveContactsMutation.isPending} title={t('forms.delete')}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </>
                        )}
                      </TableRow>
                    ))}
                    {newContact && (
                      <TableRow>
                        <TableCell>
                          <Input autoFocus value={newContactValues.responsible} placeholder={t('devices.useful_info_page.col_responsible')} onChange={e => setNewContactValues(v => ({ ...v, responsible: e.target.value }))} />
                        </TableCell>
                        <TableCell>
                          <Input value={newContactValues.clapBase} placeholder={t('devices.useful_info_page.col_clapbase')} onChange={e => setNewContactValues(v => ({ ...v, clapBase: e.target.value }))} />
                        </TableCell>
                        <TableCell>
                          <Textarea rows={5} value={newContactValues.data} placeholder={t('devices.useful_info_page.col_data')} onChange={e => setNewContactValues(v => ({ ...v, data: e.target.value }))} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={saveNewContact} disabled={saveContactsMutation.isPending} title={t('forms.submit')}>
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setNewContact(null)} title={t('forms.cancel')}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Modelos para Ingresos */}
        <TabsContent value="models">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('devices.useful_info_page.models_tab')}</CardTitle>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={addModel}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('devices.useful_info_page.add_row')}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[260px]">{t('devices.useful_info_page.col_profile')}</TableHead>
                      <TableHead>{t('devices.useful_info_page.col_model')}</TableHead>
                      {canEdit && <TableHead className="w-[100px] text-right">{t('devices.table_headers.actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {models.map(m => (
                      <TableRow key={m.id}>
                        {editingModelId === m.id ? (
                          <>
                            <TableCell>
                              <Input value={editModelValues.profile} onChange={e => setEditModelValues(v => ({ ...v, profile: e.target.value }))} />
                            </TableCell>
                            <TableCell>
                              <Input value={editModelValues.model} onChange={e => setEditModelValues(v => ({ ...v, model: e.target.value }))} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" onClick={saveModel} disabled={saveModelsMutation.isPending} title={t('forms.submit')}>
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingModelId(null)} title={t('forms.cancel')}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="font-medium">{m.profile}</TableCell>
                            <TableCell>{m.model}</TableCell>
                            {canEdit && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => startEditModel(m)} title={t('devices.edit_title')}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteModel(m.id)} disabled={saveModelsMutation.isPending} title={t('forms.delete')}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </>
                        )}
                      </TableRow>
                    ))}
                    {newModel && (
                      <TableRow>
                        <TableCell>
                          <Input autoFocus value={newModelValues.profile} placeholder={t('devices.useful_info_page.col_profile')} onChange={e => setNewModelValues(v => ({ ...v, profile: e.target.value }))} />
                        </TableCell>
                        <TableCell>
                          <Input value={newModelValues.model} placeholder={t('devices.useful_info_page.col_model')} onChange={e => setNewModelValues(v => ({ ...v, model: e.target.value }))} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={saveNewModel} disabled={saveModelsMutation.isPending} title={t('forms.submit')}>
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setNewModel(null)} title={t('forms.cancel')}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Recuperos de Computadoras */}
        <TabsContent value="recoveries">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('devices.useful_info_page.recoveries_tab')}</CardTitle>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={addRecovery}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t('devices.useful_info_page.add_row')}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[220px]">{t('devices.useful_info_page.col_responsible')}</TableHead>
                      <TableHead>{t('devices.useful_info_page.col_info')}</TableHead>
                      {canEdit && <TableHead className="w-[100px] text-right">{t('devices.table_headers.actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recoveries.map(r => (
                      <TableRow key={r.id}>
                        {editingRecoveryId === r.id ? (
                          <>
                            <TableCell>
                              <Input value={editRecoveryValues.responsible} onChange={e => setEditRecoveryValues(v => ({ ...v, responsible: e.target.value }))} />
                            </TableCell>
                            <TableCell>
                              <Textarea rows={4} value={editRecoveryValues.info} onChange={e => setEditRecoveryValues(v => ({ ...v, info: e.target.value }))} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" onClick={saveRecovery} disabled={saveRecoveriesMutation.isPending} title={t('forms.submit')}>
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingRecoveryId(null)} title={t('forms.cancel')}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="font-medium align-top">{r.responsible}</TableCell>
                            <TableCell className="text-sm whitespace-pre-line align-top">{r.info}</TableCell>
                            {canEdit && (
                              <TableCell className="text-right align-top">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => startEditRecovery(r)} title={t('devices.edit_title')}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteRecovery(r.id)} disabled={saveRecoveriesMutation.isPending} title={t('forms.delete')}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </>
                        )}
                      </TableRow>
                    ))}
                    {newRecovery && (
                      <TableRow>
                        <TableCell>
                          <Input autoFocus value={newRecoveryValues.responsible} placeholder={t('devices.useful_info_page.col_responsible')} onChange={e => setNewRecoveryValues(v => ({ ...v, responsible: e.target.value }))} />
                        </TableCell>
                        <TableCell>
                          <Textarea rows={4} value={newRecoveryValues.info} placeholder={t('devices.useful_info_page.col_info')} onChange={e => setNewRecoveryValues(v => ({ ...v, info: e.target.value }))} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={saveNewRecovery} disabled={saveRecoveriesMutation.isPending} title={t('forms.submit')}>
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setNewRecovery(null)} title={t('forms.cancel')}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {recoveries.length === 0 && !newRecovery && (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 3 : 2} className="h-24 text-center text-muted-foreground">
                          {t('devices.useful_info_page.no_recoveries')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
